import { App, Button, Form, Select, Tag } from 'antd';
import { useTranslation } from 'translations/hooks';
import { useMemo, useState } from 'react';
import {
  ScanMode,
  scanSiteSchema,
  type ScanSiteSchema,
} from 'technical/local-storage/types';
import {
  getLocalStorageValue,
  insertOrUpdateLocalStorageValue,
} from 'technical/local-storage';
import { SCAN_SITE_KEY } from 'technical/local-storage/constants';
import { useNavigate } from 'react-router';
import { Routes } from 'business/router/constants';
import { useSyncSettings } from 'business/settings/hooks/use-sync-settings';
import { useGetScanSitesData } from 'business/auth/pages/session-info/hooks/data/use-get-scan-sites-data';
import { SCAN_SITE_UPDATED } from 'technical/event/constants';

import { db } from 'technical/db';

interface ScanSiteFormValues {
  scanSite: string;
}

export const ScanSite = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const scanSite = getLocalStorageValue<ScanSiteSchema>({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });

  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
    scanSite?.id,
  );
  const { synchronize, isSyncing } = useSyncSettings();

  const [form] = Form.useForm<ScanSiteFormValues>();

  const { data: scanSitesData } = useGetScanSitesData();

  const scanSiteOptions = useMemo(() => {
    const options =
      scanSitesData?.scanSites?.map((site) => ({
        label: site.label,
        value: site.id,
      })) || [];

    return options;
  }, [scanSitesData]);

  const selectedSite = useMemo(
    () => scanSitesData?.scanSites?.find((site) => site.id === selectedSiteId),
    [scanSitesData, selectedSiteId],
  );

  const handleSaveScanSite = async (row: ScanSiteFormValues) => {
    const scanSite = scanSitesData?.scanSites?.find(
      (site) => site.id === row.scanSite,
    );

    if (!scanSite) {
      message.warning(t('settings.scanSite.scanSiteNotFound'));
      return;
    }

    insertOrUpdateLocalStorageValue<ScanSiteSchema>({
      key: SCAN_SITE_KEY,
      value: {
        id: scanSite.id,
        label: scanSite.label,
        scanMode: scanSite.scanMode as ScanMode,
        isQuarantineEnabled: scanSite.isQuarantineEnabled ?? true,
        isOnHoldOptionEnabled: scanSite.isOnHoldOptionEnabled ?? false,
      },
    });

    window.dispatchEvent(new Event(SCAN_SITE_UPDATED));
    await synchronize();

    await db.delete();

    navigate(Routes.App);
  };

  return (
    <Form<ScanSiteFormValues>
      form={form}
      onFinish={handleSaveScanSite}
      className="flex w-full flex-col"
      initialValues={scanSite ? { scanSite: scanSite.id } : {}}
    >
      <div className="flex w-full flex-col">
        <h1 className="text-primary pb-4 text-lg">
          {t('settings.scanSite.form.title')}
        </h1>

        <Form.Item<ScanSiteFormValues>
          name="scanSite"
          label={t('settings.scanSite.form.label')}
          rules={[{ required: true, message: t('common.fieldRequired') }]}
        >
          <Select
            placeholder={t('common.placeholder.select')}
            className="w-full text-base"
            options={scanSiteOptions}
            onChange={(value) => setSelectedSiteId(value)}
            value={selectedSiteId}
          />
        </Form.Item>

        <div className="min-h-16">
          {selectedSite ? (
            <div className="py-2">
              <div className="text-primary font-bold">
                {t('settings.scanSite.form.accessLabel')}
              </div>
              {selectedSite.scanMode === ScanMode.RECEIPT ? (
                <>
                  {t('home.scanMode.receipt.title')}
                  <div className="flex gap-4 text-base">
                    <Tag
                      variant="filled"
                      className="bg-light-blue text-sm text-gray-600"
                    >
                      {t('settings.scanSite.form.quarantine')}:{' '}
                      {(selectedSite.isQuarantineEnabled ?? true)
                        ? t('settings.scanSite.form.enabled')
                        : t('settings.scanSite.form.disabled')}
                    </Tag>
                    <Tag
                      variant="filled"
                      className="bg-light-blue text-sm text-gray-600"
                    >
                      {t('settings.scanSite.form.onHold')}:{' '}
                      {(selectedSite.isOnHoldOptionEnabled ?? true)
                        ? t('settings.scanSite.form.enabled')
                        : t('settings.scanSite.form.disabled')}
                    </Tag>
                  </div>
                </>
              ) : (
                <div className="text-base">
                  {t('home.scanMode.tunnel.title')}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="w-full">
        <Button
          type="primary"
          className="w-full"
          htmlType="submit"
          loading={isSyncing}
        >
          {t('common.validate')}
        </Button>
      </div>
    </Form>
  );
};
